import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Car, Truck, Bike, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const VEHICLE_TYPE_META = {
  livianos: { label: 'Vehículos Livianos', icon: Car, description: 'Autos, SUVs, pickups pequeñas' },
  pesados: { label: 'Vehículos Pesados', icon: Truck, description: 'Camiones, buses, maquinaria' },
  motos: { label: 'Motocicletas', icon: Bike, description: 'Motos, cuadraciclos eléctricos' },
};

const TallerSettingsTab = ({ API, sessionToken }) => {
  const [selected, setSelected] = useState([]);
  const [original, setOriginal] = useState([]);
  const [allowed, setAllowed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const headers = { Authorization: `Bearer ${sessionToken}` };

  useEffect(() => { fetchTypes(); }, []);

  const fetchTypes = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/taller/vehicle-types`, { headers });
      setSelected(res.data.vehicle_types || []);
      setOriginal(res.data.vehicle_types || []);
      setAllowed(res.data.allowed || []);
    } catch (e) {
      toast.error('Error al cargar la configuración');
    } finally {
      setLoading(false);
    }
  };

  const toggle = (t) => {
    if (selected.includes(t)) {
      setSelected(selected.filter(x => x !== t));
    } else {
      setSelected([...selected, t]);
    }
  };

  const save = async () => {
    if (selected.length === 0) {
      toast.error('Selecciona al menos un tipo de vehículo');
      return;
    }
    try {
      setSaving(true);
      await axios.put(`${API}/taller/profile/vehicle-types`, { vehicle_types: selected }, { headers });
      setOriginal(selected);
      toast.success('Tipos de vehículo actualizados');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = JSON.stringify(selected.sort()) !== JSON.stringify([...original].sort());

  if (loading) return <div className="text-sm text-slate-500 p-6">Cargando configuración…</div>;

  return (
    <div className="space-y-6" data-testid="taller-settings-tab">
      <Card className="p-6">
        <div className="mb-5">
          <h3 className="text-lg font-semibold text-slate-900">Tipos de vehículos que atiendes</h3>
          <p className="text-sm text-slate-500 mt-1">
            Selecciona los tipos de vehículos en los que tu taller se especializa. Los clientes podrán filtrar talleres por estos tipos.
          </p>
        </div>

        <div className="grid sm:grid-cols-3 gap-3">
          {allowed.map((t) => {
            const meta = VEHICLE_TYPE_META[t] || { label: t, icon: Car, description: '' };
            const Icon = meta.icon;
            const isSelected = selected.includes(t);
            return (
              <button
                key={t}
                type="button"
                onClick={() => toggle(t)}
                className={`text-left rounded-xl border-2 p-4 transition-all ${
                  isSelected
                    ? 'border-purple-600 bg-purple-50 shadow-sm'
                    : 'border-slate-200 hover:border-purple-300 hover:bg-slate-50'
                }`}
                data-testid={`vehicle-type-${t}`}
              >
                <div className="flex items-start justify-between">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isSelected ? 'bg-purple-600 text-white' : 'bg-slate-100 text-slate-600'}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  {isSelected && <CheckCircle2 className="w-5 h-5 text-purple-600" />}
                </div>
                <p className="mt-3 font-medium text-slate-900">{meta.label}</p>
                <p className="text-xs text-slate-500 mt-1">{meta.description}</p>
              </button>
            );
          })}
        </div>

        {selected.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="text-xs text-slate-500">Seleccionados:</span>
            {selected.map((t) => (
              <Badge key={t} variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                {VEHICLE_TYPE_META[t]?.label || t}
              </Badge>
            ))}
          </div>
        )}

        <div className="mt-6 flex justify-end gap-2">
          {hasChanges && (
            <Button variant="outline" onClick={() => setSelected(original)}>
              Cancelar
            </Button>
          )}
          <Button
            onClick={save}
            disabled={!hasChanges || saving || selected.length === 0}
            className="bg-purple-600 hover:bg-purple-700"
            data-testid="save-vehicle-types-btn"
          >
            {saving ? 'Guardando…' : 'Guardar cambios'}
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default TallerSettingsTab;
